import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Organization } from '@/types/organization'
import type { UserOrganization } from '@/firebase/services/organizations.service'
import { useAuthStore } from './auth'
import { 
  getUserOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  addUserToOrganization,
  removeUserFromOrganization,
  updateUserRole
} from '@/firebase/services/organizations.service'

export interface OrganizationWithRole extends Organization {
  role: string
}

export const useOrganizationStore = defineStore('organization', () => {
  const organizations = ref<OrganizationWithRole[]>([])
  const userOrganizations = ref<UserOrganization[]>([])
  const loading = ref(false)

  const getCurrentUser = () => {
    const authStore = useAuthStore()
    return authStore.user
  }

  const fetchOrganizations = async () => {
    loading.value = true
    const user = getCurrentUser()

    if (!user) {
      console.error('[Organization Store] Usuário não autenticado')
      loading.value = false
      organizations.value = []
      return []
    }

    try {
      console.log('[Organization Store] Buscando organizações do usuário')
      const orgs = await getUserOrganizations(user.uid)
      console.log('[Organization Store] Organizações encontradas:', orgs)
      organizations.value = orgs
      return orgs
    } catch (error) {
      console.error('[Organization Store] Erro ao buscar organizações:', error)
      organizations.value = []
      return []
    } finally {
      loading.value = false
    }
  }

  const createNewOrganization = async (data: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>) => {
    loading.value = true
    const user = getCurrentUser()

    if (!user) {
      console.error('[Organization Store] Usuário não autenticado')
      loading.value = false
      throw new Error('Usuário não autenticado')
    }

    try {
      console.log('[Organization Store] Criando nova organização:', data)
      const org = await createOrganization(user.uid, data)
      await fetchOrganizations()
      return org
    } catch (error) {
      console.error('[Organization Store] Erro ao criar organização:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const updateExistingOrganization = async (id: string, data: Partial<Organization>) => {
    loading.value = true
    try {
      await updateOrganization(id, data)
      await fetchOrganizations()
    } catch (error) {
      console.error('[Organization Store] Erro ao atualizar organização:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const deleteExistingOrganization = async (id: string) => {
    loading.value = true
    try {
      await deleteOrganization(id)
      await fetchOrganizations()
    } catch (error) {
      console.error('[Organization Store] Erro ao deletar organização:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  return {
    organizations,
    userOrganizations,
    loading,
    fetchOrganizations,
    createNewOrganization,
    updateExistingOrganization,
    deleteExistingOrganization
  }
})
